import pixels from 'image-pixels';
import {
  BaseColorTextures,
  MaskTextures,
  NormalTextures,
  RoughnessTextures,
  SpecularTextures,
} from './constants/avatarTexturesConstants';
import {
  type AvatarTexturesData,
  type AvatarTexturesDesc,
  AvatarTextureLayer,
} from './types/avatarTexturesTypes';

/**
 * Texture layer.
 */
interface TextureLayer {
  texture: Uint8ClampedArray;
  mask?: Uint8ClampedArray;
}

/**
 * Merges two texture layers.
 * @param base - Base layer.
 * @param next - Next layer.
 * @returns Base layer modified.
 */
function blendTextureInPlace(base: Uint8ClampedArray, layer: TextureLayer): Uint8ClampedArray {
  const mask = layer.mask!;
  const next = layer.texture;
  const arrLength = mask.length;

  for (let i = 0; i < arrLength; i += 4) {
    const maskValue = mask[i] / 255;
    // TODO: Support single channel textures
    const baseR = base[i];
    const nextR = next[i];
    base[i] = baseR * (1 - maskValue) + nextR * maskValue;
    const baseG = base[i + 1];
    const nextG = next[i + 1];
    base[i + 1] = baseG * (1 - maskValue) + nextG * maskValue;
    const baseB = base[i + 2];
    const nextB = next[i + 2];
    base[i + 2] = baseB * (1 - maskValue) + nextB * maskValue;
    base[i + 3] = 255;
  }

  return base;
}

/**
 * Blend texture layers.
 * @param layer0 - Base layer.
 * @param layers - Next layers.
 * @returns New texture with layers combined.
 */
function blendTextures(layer0: Uint8ClampedArray, layers: TextureLayer[]): Uint8ClampedArray {
  // Return when no layers
  if (!layers.length) {
    return layer0;
  }

  // Blend layers in-place
  const result = new Uint8ClampedArray(layer0);

  for (const layer of layers) {
    blendTextureInPlace(result, layer);
  }

  return result;
}

/**
 * Build avatar textures from descriptor.
 * @param desc - Textures descriptor.
 */
export async function buildAvatarTextures(desc: AvatarTexturesDesc): Promise<AvatarTexturesData> {
  // Call pixels to retrieve raw image data
  // NOTE: pixels seems to cache internally requests returning the same object
  async function fetchTexture(source: string): Promise<Uint8ClampedArray | undefined> {
    if (!source) {
      return undefined;
    }
    const raw = await pixels(source);
    const data = raw.data;
    return data;
  }

  const textures = {
    skin: {
      baseColor: BaseColorTextures[AvatarTextureLayer.SKIN][desc.sex][desc.skin],
      normal: NormalTextures[AvatarTextureLayer.SKIN][desc.skin],
      roughness: RoughnessTextures[AvatarTextureLayer.SKIN][desc.skin],
      specular: SpecularTextures[AvatarTextureLayer.SKIN][desc.skin],
    },
    top: {
      baseColor:
        BaseColorTextures[AvatarTextureLayer.TOP][desc.coverageColor][desc.sex][desc.coverageTop],
      normal: NormalTextures[AvatarTextureLayer.TOP][desc.sex][desc.coverageTop],
      roughness: RoughnessTextures[AvatarTextureLayer.TOP][desc.sex][desc.coverageTop],
      specular: SpecularTextures[AvatarTextureLayer.TOP][desc.sex][desc.coverageTop],
      mask: MaskTextures[AvatarTextureLayer.TOP][desc.sex][desc.coverageTop],
    },
    bottom: {
      baseColor:
        BaseColorTextures[AvatarTextureLayer.BOTTOM][desc.coverageColor][desc.sex][
          desc.coverageBottom
        ],
      normal: NormalTextures[AvatarTextureLayer.BOTTOM][desc.sex][desc.coverageBottom],
      roughness: RoughnessTextures[AvatarTextureLayer.BOTTOM][desc.sex][desc.coverageBottom],
      specular: SpecularTextures[AvatarTextureLayer.BOTTOM][desc.sex][desc.coverageBottom],
      mask: MaskTextures[AvatarTextureLayer.BOTTOM][desc.sex][desc.coverageBottom],
    },
  };

  // Load all sources in parallel.
  const promises = [];
  promises.push(fetchTexture(textures.skin.baseColor));
  promises.push(fetchTexture(textures.skin.normal));
  promises.push(fetchTexture(textures.skin.roughness));
  promises.push(fetchTexture(textures.skin.specular));

  if (!desc.isBottomDisabled) {
    promises.push(fetchTexture(textures.bottom.baseColor));
    promises.push(fetchTexture(textures.bottom.normal));
    promises.push(fetchTexture(textures.bottom.roughness));
    promises.push(fetchTexture(textures.bottom.specular));
    promises.push(fetchTexture(textures.bottom.mask));
  }
  if (!desc.isTopDisabled) {
    promises.push(fetchTexture(textures.top.baseColor));
    promises.push(fetchTexture(textures.top.normal));
    promises.push(fetchTexture(textures.top.roughness));
    promises.push(fetchTexture(textures.top.specular));
    promises.push(fetchTexture(textures.top.mask));
  }

  const sources = await Promise.all(promises);

  const skinBaseColor = sources[0]!;
  const skinNormalTexture = sources[1]!;
  const skinRoughnessTexture = sources[2]!;
  const skinSpecularTexture = sources[3]!;

  let idx = 4;
  let bottomBaseColor: Uint8ClampedArray | undefined;
  let bottomNormalTexture: Uint8ClampedArray | undefined;
  let bottomRoughnessTexture: Uint8ClampedArray | undefined;
  let bottomSpecularTexture: Uint8ClampedArray | undefined;
  let bottomMask: Uint8ClampedArray | undefined;
  let topBaseColor: Uint8ClampedArray | undefined;
  let topNormalTexture: Uint8ClampedArray | undefined;
  let topRoughnessTexture: Uint8ClampedArray | undefined;
  let topSpecularTexture: Uint8ClampedArray | undefined;
  let topMask: Uint8ClampedArray | undefined;

  if (!desc.isBottomDisabled) {
    bottomBaseColor = sources[idx++];
    bottomNormalTexture = sources[idx++];
    bottomRoughnessTexture = sources[idx++];
    bottomSpecularTexture = sources[idx++];
    bottomMask = sources[idx++];
  }
  if (!desc.isTopDisabled) {
    topBaseColor = sources[idx++];
    topNormalTexture = sources[idx++];
    topRoughnessTexture = sources[idx++];
    topSpecularTexture = sources[idx++];
    topMask = sources[idx++];
  }

  const isShowBottom = Boolean(bottomBaseColor);
  const isShowTop = Boolean(topBaseColor);

  const [baseColor, normal, roughness, specular] = await Promise.all([
    Promise.resolve(
      blendTextures(skinBaseColor, [
        ...(isShowBottom ? [{ texture: bottomBaseColor!, mask: bottomMask }] : []),
        ...(isShowTop ? [{ texture: topBaseColor!, mask: topMask }] : []),
      ]),
    ),
    Promise.resolve(
      blendTextures(skinNormalTexture, [
        ...(isShowBottom ? [{ texture: bottomNormalTexture!, mask: bottomMask }] : []),
        ...(isShowTop ? [{ texture: topNormalTexture!, mask: topMask }] : []),
      ]),
    ),
    Promise.resolve(
      blendTextures(skinRoughnessTexture, [
        ...(isShowBottom ? [{ texture: bottomRoughnessTexture!, mask: bottomMask }] : []),
        ...(isShowTop ? [{ texture: topRoughnessTexture!, mask: topMask }] : []),
      ]),
    ),
    Promise.resolve(
      blendTextures(skinSpecularTexture, [
        ...(isShowBottom ? [{ texture: bottomSpecularTexture!, mask: bottomMask }] : []),
        ...(isShowTop ? [{ texture: topSpecularTexture!, mask: topMask }] : []),
      ]),
    ),
  ]);

  return { baseColor, normal, roughness, specular };
}
